import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import TaxonomyManager from "../components/TaxonomyManager";
import { useTaxonomy } from "../hooks/useTaxonomy";
import {
  Plus, MapPin, Navigation, Download, FileText, Trash2, Edit2,
  ChevronLeft, ChevronRight, Bookmark, Zap, AlertTriangle, Car, Loader2, DollarSign
} from "lucide-react";

const FALLBACK_CAT_LABELS = { commute: "Work Commute", clinic: "Multi-Clinic Travel", ce: "CE / Course Travel", other: "Other Professional" };
const CAT_COLORS = { commute: "#6B4E8A", clinic: "#8DB5A0", ce: "#8DB5A0", ce_event: "#8DB5A0", between_offices: "#8DB5A0", other: "#64748B" };

const MileagePage = () => {
  const { user } = useAuth();
  const { items: taxonomyCats, refetch: refetchCats } = useTaxonomy("mileage_categories");
  // Compose the active label map: taxonomy items override the fallback keys.
  const CAT_LABELS = taxonomyCats.length > 0
    ? Object.fromEntries(taxonomyCats.map(i => [i.key, i.label]))
    : FALLBACK_CAT_LABELS;
  const [dashboard, setDashboard] = useState(null);
  const [entries, setEntries] = useState([]);
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showSaveRoute, setShowSaveRoute] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    from_location: "", to_location: "", distance_km: "",
    category: "commute", notes: "", is_round_trip: false,
    start_address: "", destination_name: "", province: user?.province || "",
  });
  const [provinces, setProvinces] = useState([]);
  const [routeForm, setRouteForm] = useState({ name: "", from_location: "", to_location: "", distance_km: "", category: "commute" });

  useEffect(() => { fetchAll(); }, [selectedYear]);
  useEffect(() => {
    axios.get(`${API}/provinces`).then(r => setProvinces(r.data)).catch(() => {});
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dashRes, entRes, routeRes] = await Promise.all([
        axios.get(`${API}/mileage/dashboard?year=${selectedYear}`),
        axios.get(`${API}/mileage/entries?year=${selectedYear}`),
        axios.get(`${API}/mileage/saved-routes`)
      ]);
      setDashboard(dashRes.data);
      setEntries(entRes.data.entries);
      setSavedRoutes(routeRes.data);
    } catch (error) {
      toast.error("Failed to load mileage data");
    } finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.from_location || !form.to_location || !form.distance_km) {
      toast.error("Fill in locations and distance"); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/mileage/entries`, { ...form, distance_km: parseFloat(form.distance_km) });
      toast.success("Trip logged!");
      setShowAdd(false);
      setForm({ date: new Date().toISOString().split("T")[0], from_location: "", to_location: "", distance_km: "", category: "commute", notes: "", is_round_trip: false, start_address: "", destination_name: "", province: user?.province || "" });
      fetchAll();
    } catch (error) { toast.error("Failed to log trip"); }
    finally { setSubmitting(false); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingEntry) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/mileage/entries/${editingEntry.mileage_id}`, {
        date: editingEntry.date, from_location: editingEntry.from_location,
        to_location: editingEntry.to_location, distance_km: parseFloat(editingEntry.distance_km),
        category: editingEntry.category, notes: editingEntry.notes
      });
      toast.success("Trip updated!");
      setEditingEntry(null);
      fetchAll();
    } catch (error) { toast.error("Failed to update"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this trip?")) return;
    try { await axios.delete(`${API}/mileage/entries/${id}`); toast.success("Deleted"); fetchAll(); }
    catch { toast.error("Failed to delete"); }
  };

  const handleEstimate = async () => {
    if (!form.from_location || !form.to_location) { toast.error("Enter both locations first"); return; }
    setEstimating(true);
    try {
      const res = await axios.post(`${API}/mileage/estimate-distance`, { from_location: form.from_location, to_location: form.to_location });
      setForm(f => ({ ...f, distance_km: String(res.data.distance_km) }));
      if (res.data.note) toast.info(res.data.note);
      else toast.success(`Estimated: ${res.data.distance_km} km`);
    } catch (error) { toast.error(error.response?.data?.detail || "Could not estimate"); }
    finally { setEstimating(false); }
  };

  const handleSaveRoute = async (e) => {
    e.preventDefault();
    if (!routeForm.name || !routeForm.from_location || !routeForm.to_location || !routeForm.distance_km) {
      toast.error("Fill in all fields"); return;
    }
    try {
      await axios.post(`${API}/mileage/saved-routes`, { ...routeForm, distance_km: parseFloat(routeForm.distance_km) });
      toast.success("Route saved!");
      setShowSaveRoute(false);
      setRouteForm({ name: "", from_location: "", to_location: "", distance_km: "", category: "commute" });
      fetchAll();
    } catch (error) { toast.error("Failed to save route"); }
  };

  const handleQuickLog = async (route) => {
    try {
      await axios.post(`${API}/mileage/saved-routes/${route.route_id}/log`, {
        date: new Date().toISOString().split("T")[0], is_round_trip: false
      });
      toast.success(`Logged: ${route.name}`);
      fetchAll();
    } catch (error) { toast.error("Failed to log"); }
  };

  const handleDeleteRoute = async (id) => {
    try { await axios.delete(`${API}/mileage/saved-routes/${id}`); toast.success("Route removed"); fetchAll(); }
    catch { toast.error("Failed"); }
  };

  const handleUseSaved = (route) => {
    setForm(f => ({ ...f, from_location: route.from_location, to_location: route.to_location, distance_km: String(route.distance_km), category: route.category }));
    toast.info(`Loaded route: ${route.name}`);
  };

  const topCats = dashboard?.category_breakdown ? Object.entries(dashboard.category_breakdown).sort((a, b) => b[1] - a[1]) : [];

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="mileage-page">
        <div className="bg-[#F0ECF7]/70 border border-[#E5DFEC] rounded-xl p-3 mb-6 flex items-start gap-2" data-testid="mileage-disclaimer">
          <AlertTriangle className="w-4 h-4 text-[#6B4E8A] mt-0.5 shrink-0" />
          <p className="text-xs text-[#6B4E8A] leading-relaxed">
            Mileage rates are based on current CRA guidelines and are subject to change. DHDH is not responsible for discrepancies between tracked amounts and CRA-accepted claims.
          </p>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <EditableText
                blockKey="mileage.title"
                fallback="Mileage Tracker"
                as="span"
              />
              <TaxonomyManager
                kind="mileage_categories"
                title="Mileage purpose tags"
                description="Edit the categories members can tag a trip with."
                onChange={refetchCats}
                compact
              />
            </h1>
            <p className="text-[#64748B] mt-1">Log trips, track kilometers, export reports</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Dialog open={showSaveRoute} onOpenChange={setShowSaveRoute}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="save-route-btn"><Bookmark className="w-4 h-4 mr-2" /> Save Route</Button>
              </DialogTrigger>
              <DialogContent className="max-w-sm">
                <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Save a Route</DialogTitle></DialogHeader>
                <form onSubmit={handleSaveRoute} className="space-y-4 mt-4">
                  <div><Label>Route Name</Label><Input value={routeForm.name} onChange={e => setRouteForm({...routeForm, name: e.target.value})} placeholder="e.g. Home to Clinic A" className="mt-2 bg-white border-[#E5DFEC]" data-testid="route-name-input" /></div>
                  <div><Label>From</Label><Input value={routeForm.from_location} onChange={e => setRouteForm({...routeForm, from_location: e.target.value})} placeholder="Starting location" className="mt-2 bg-white border-[#E5DFEC]" /></div>
                  <div><Label>To</Label><Input value={routeForm.to_location} onChange={e => setRouteForm({...routeForm, to_location: e.target.value})} placeholder="Destination" className="mt-2 bg-white border-[#E5DFEC]" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Distance (km)</Label><Input type="number" step="0.1" value={routeForm.distance_km} onChange={e => setRouteForm({...routeForm, distance_km: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" /></div>
                    <div><Label>Category</Label>
                      <Select value={routeForm.category} onValueChange={v => setRouteForm({...routeForm, category: v})}><SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(CAT_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setShowSaveRoute(false)} className="rounded-full">Cancel</Button><Button type="submit" className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="submit-route-btn">Save</Button></div>
                </form>
              </DialogContent>
            </Dialog>
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
              <DialogTrigger asChild>
                <Button className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="log-trip-btn"><Plus className="w-4 h-4 mr-2" /> Log Trip</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Log a Trip</DialogTitle></DialogHeader>
                <form onSubmit={handleAdd} className="space-y-4 mt-4">
                  <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" data-testid="trip-date-input" /></div>
                  {savedRoutes.length > 0 && (
                    <div>
                      <Label className="text-xs text-[#64748B]">Quick fill from saved route:</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {savedRoutes.slice(0,4).map(r => (
                          <button key={r.route_id} type="button" onClick={() => handleUseSaved(r)} className="text-xs px-3 py-1.5 bg-[#F0ECF7] text-[#6B4E8A] rounded-full hover:bg-[#E5DFEC]">{r.name}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div><Label>From</Label><Input value={form.from_location} onChange={e => setForm({...form, from_location: e.target.value})} placeholder="Starting location" className="mt-2 bg-white border-[#E5DFEC]" data-testid="trip-from-input" /></div>
                  <div><Label className="text-xs text-[#64748B]">Start address <span className="text-[#94A3B8]">(optional)</span></Label><Input value={form.start_address} onChange={e => setForm({...form, start_address: e.target.value})} placeholder="123 Main St, Calgary" className="mt-2 bg-white border-[#E5DFEC]" data-testid="trip-start-address-input" /></div>
                  <div><Label>To</Label><Input value={form.to_location} onChange={e => setForm({...form, to_location: e.target.value})} placeholder="Destination" className="mt-2 bg-white border-[#E5DFEC]" data-testid="trip-to-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-xs text-[#64748B]">Destination name <span className="text-[#94A3B8]">(optional)</span></Label><Input value={form.destination_name} onChange={e => setForm({...form, destination_name: e.target.value})} placeholder="e.g. Smile Bright Dental" className="mt-2 bg-white border-[#E5DFEC]" data-testid="trip-destination-name-input" /></div>
                    <div><Label>Province</Label>
                      <Select value={form.province || "none"} onValueChange={v => setForm({...form, province: v === "none" ? "" : v})}>
                        <SelectTrigger className="mt-2 bg-white border-[#E5DFEC]" data-testid="trip-province-select"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— None —</SelectItem>
                          {provinces.map(p => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Distance (km)</Label>
                      <div className="flex gap-2 mt-2">
                        <Input type="number" step="0.1" min="0" value={form.distance_km} onChange={e => setForm({...form, distance_km: e.target.value})} className="bg-white border-[#E5DFEC] flex-1" placeholder="0.0" data-testid="trip-distance-input" />
                        <Button type="button" variant="outline" size="sm" onClick={handleEstimate} disabled={estimating} className="shrink-0 text-xs rounded-full border-[#8DB5A0] text-[#8DB5A0] hover:bg-[#8DB5A0]/10" data-testid="estimate-btn">
                          {estimating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                        </Button>
                      </div>
                    </div>
                    <div><Label>Purpose</Label>
                      <Select value={form.category} onValueChange={v => setForm({...form, category: v})}><SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(CAT_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="round" checked={form.is_round_trip} onCheckedChange={c => setForm({...form, is_round_trip: c})} />
                    <Label htmlFor="round" className="cursor-pointer text-sm">Round trip (doubles distance)</Label>
                  </div>
                  <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes" className="mt-2 bg-white border-[#E5DFEC]" /></div>
                  <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setShowAdd(false)} className="rounded-full">Cancel</Button><Button type="submit" className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" disabled={submitting} data-testid="submit-trip-btn">{submitting ? "Saving..." : "Log Trip"}</Button></div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="space-y-6">{[1,2,3].map(i => <div key={i} className="h-28 bg-[#F0ECF7] rounded-2xl animate-pulse" />)}</div>
        ) : (
          <>
            {/* Year & Export */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <button onClick={() => setSelectedYear(y => y-1)} className="p-2 hover:bg-[#F0ECF7] rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
                <h2 className="text-xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedYear}</h2>
                <button onClick={() => setSelectedYear(y => y+1)} className="p-2 hover:bg-[#F0ECF7] rounded-lg"><ChevronRight className="w-5 h-5" /></button>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => window.open(`${API}/mileage/export/csv?year=${selectedYear}`, "_blank")} className="rounded-full text-sm" data-testid="mileage-csv-btn"><Download className="w-4 h-4 mr-1" /> CSV</Button>
                <Button variant="outline" onClick={() => window.open(`${API}/finance/export/pdf?year=${selectedYear}`, "_blank")} className="rounded-full text-sm" data-testid="mileage-pdf-btn"><FileText className="w-4 h-4 mr-1" /> PDF</Button>
              </div>
            </div>

            {/* Stats */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-gradient-to-br from-[#10B981] to-[#059669] text-white rounded-2xl p-6">
                <Car className="w-6 h-6 mb-2 opacity-70" />
                <p className="text-3xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>{(dashboard?.total_km || 0).toLocaleString()} km</p>
                <p className="text-sm text-white/60 mt-1">{selectedYear} Total</p>
              </div>
              <div className="bg-white border border-[#E5DFEC] rounded-2xl p-6">
                <Navigation className="w-6 h-6 mb-2 text-[#8DB5A0]" />
                <p className="text-3xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{dashboard?.trip_count || 0}</p>
                <p className="text-sm text-[#64748B] mt-1">Trips</p>
              </div>
              <div className="bg-gradient-to-br from-[#6B4E8A] to-[#553D6F] text-white rounded-2xl p-6" data-testid="cra-estimate-card">
                <DollarSign className="w-6 h-6 mb-2 opacity-70" />
                <p className="text-3xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  ${(dashboard?.cra_estimate?.estimated_deduction_cad ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-sm text-white/70 mt-1">
                  Estimated CRA value{dashboard?.cra_estimate ? ` · ${(dashboard.cra_estimate.first_5000_km * 100).toFixed(0)}¢/${(dashboard.cra_estimate.additional_km * 100).toFixed(0)}¢ ${dashboard.cra_estimate.year}` : ""}
                </p>
              </div>
              <div className="bg-white border border-[#E5DFEC] rounded-2xl p-6">
                <Bookmark className="w-6 h-6 mb-2 text-[#8DB5A0]" />
                <p className="text-3xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{savedRoutes.length}</p>
                <p className="text-sm text-[#64748B] mt-1">Saved Routes</p>
              </div>
            </motion.div>

            {/* Category Breakdown */}
            {topCats.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white border border-[#E5DFEC] rounded-2xl p-6 mb-8">
                <h3 className="font-medium text-[#6B4E8A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Travel Breakdown</h3>
                <div className="space-y-3">
                  {topCats.map(([cat, km]) => {
                    const pct = dashboard?.total_km ? (km / dashboard.total_km * 100) : 0;
                    return (
                      <div key={cat}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-[#6B4E8A]">{CAT_LABELS[cat] || cat}</span>
                          <span className="text-[#64748B]">{km.toFixed(1)} km ({pct.toFixed(0)}%)</span>
                        </div>
                        <div className="w-full bg-[#F0ECF7] rounded-full h-2"><div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: CAT_COLORS[cat] || "#64748B" }} /></div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Saved Routes */}
            {savedRoutes.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white border border-[#E5DFEC] rounded-2xl p-6 mb-8">
                <h3 className="font-medium text-[#6B4E8A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Saved Routes</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {savedRoutes.map(r => (
                    <div key={r.route_id} className="flex items-center justify-between p-3 bg-[#FAF7F2] rounded-xl">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-2 h-10 rounded-full shrink-0" style={{ backgroundColor: CAT_COLORS[r.category] || "#64748B" }} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#6B4E8A] truncate">{r.name}</p>
                          <p className="text-xs text-[#64748B] truncate">{r.from_location} → {r.to_location} · {r.distance_km} km</p>
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" onClick={() => handleQuickLog(r)} className="bg-[#10B981] text-white hover:bg-[#059669] rounded-full text-xs h-7 px-3" data-testid={`quick-log-${r.route_id}`}>Log</Button>
                        <button onClick={() => handleDeleteRoute(r.route_id)} className="p-1.5 hover:bg-[#EF4444]/10 rounded-lg"><Trash2 className="w-3.5 h-3.5 text-[#EF4444]" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Trips List */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white border border-[#E5DFEC] rounded-2xl p-6">
              <h3 className="font-medium text-[#6B4E8A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Trip Log</h3>
              {entries.length > 0 ? (
                <div className="space-y-2">
                  {entries.map(e => (
                    <div key={e.mileage_id} className="flex items-center justify-between p-3 bg-[#FAF7F2] rounded-xl group">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-2 h-10 rounded-full shrink-0" style={{ backgroundColor: CAT_COLORS[e.category] || "#64748B" }} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#6B4E8A] truncate">{e.from_location} → {e.to_location}</p>
                          <p className="text-xs text-[#64748B]">{e.date} · {CAT_LABELS[e.category] || e.category}{e.notes ? ` · ${e.notes}` : ""}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-medium text-[#6B4E8A]">{e.distance_km} km</span>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => setEditingEntry(e)} className="p-1.5 hover:bg-[#E5DFEC] rounded-lg"><Edit2 className="w-3.5 h-3.5 text-[#64748B]" /></button>
                          <button onClick={() => handleDelete(e.mileage_id)} className="p-1.5 hover:bg-[#EF4444]/10 rounded-lg"><Trash2 className="w-3.5 h-3.5 text-[#EF4444]" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MapPin className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                  <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No trips logged</h3>
                  <p className="text-[#64748B] mb-4">Start tracking your professional mileage</p>
                </div>
              )}
            </motion.div>
          </>
        )}

        {/* Edit Trip Dialog */}
        <Dialog open={!!editingEntry} onOpenChange={() => setEditingEntry(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Edit Trip</DialogTitle></DialogHeader>
            {editingEntry && (
              <form onSubmit={handleUpdate} className="space-y-4 mt-4">
                <div><Label>Date</Label><Input type="date" value={editingEntry.date} onChange={e => setEditingEntry({...editingEntry, date: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" /></div>
                <div><Label>From</Label><Input value={editingEntry.from_location} onChange={e => setEditingEntry({...editingEntry, from_location: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" /></div>
                <div><Label>To</Label><Input value={editingEntry.to_location} onChange={e => setEditingEntry({...editingEntry, to_location: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Distance (km)</Label><Input type="number" step="0.1" value={editingEntry.distance_km} onChange={e => setEditingEntry({...editingEntry, distance_km: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" /></div>
                  <div><Label>Category</Label><Select value={editingEntry.category} onValueChange={v => setEditingEntry({...editingEntry, category: v})}><SelectTrigger className="mt-2 bg-white border-[#E5DFEC]"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(CAT_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div><Label>Notes</Label><Textarea value={editingEntry.notes || ""} onChange={e => setEditingEntry({...editingEntry, notes: e.target.value})} className="mt-2 bg-white border-[#E5DFEC]" /></div>
                <div className="flex justify-end gap-3"><Button type="button" variant="outline" onClick={() => setEditingEntry(null)} className="rounded-full">Cancel</Button><Button type="submit" className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" disabled={submitting}>Update</Button></div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MileagePage;
